import { MediaAtPlacement } from '@/components/commerce/media/CommercialMediaGallery';
import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { Link, Outlet, useSearchParams, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { StorePageContext } from '@/layouts/storefrontContext';
import type { PublicPagePayload } from '@/hooks/storefront/useStorefrontApi';
import { isRestaurantStorefront } from '@/lib/storefrontFulfillment';
import { storefrontTheme } from '@/lib/storefrontTheme';
import { resolvePageRestaurantPalette } from '@/lib/restaurantPalettes';
import {
  ArrowLeft,
  Check,
  History,
  Home,
  Mail,
  MessageCircle,
  Phone,
  Search,
  ShoppingBag,
} from 'lucide-react';
import { cartCount, selectCartLines, useStorefrontCart } from '@/stores/storefrontCart';
import { StorefrontHeader } from '@/pages/storefront/StorefrontHeader';
import { StoreStrictTableClaimDialog } from '@/pages/storefront/StoreTableBanner';
import { StorefrontFlowline } from '@/pages/storefront/StorefrontFlowline';

/**
 * Layout léger du storefront public (shop.velko-pos.com ; local /shop/*).
 *
 * AUCUNE dépendance auth/ShopContext : les visiteurs ne sont pas connectés.
 * La page résolue publie son contexte (branding + basePath pour les liens
 * internes panier/checkout) via StorePageContext.
 */

export default function StorefrontLayout() {
  const { t } = useTranslation('storefront');
  const [state, setState] = useState<{ page: PublicPagePayload | null; basePath: string }>({
    page: null,
    basePath: '',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [isSearchOpen, setIsSearchOpen] = useState(!!searchParams.get('q'));
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '');

  useEffect(() => {
    setSearchInput(searchParams.get('q') ?? '');
  }, [searchParams]);

  // Réinitialise le contexte de boutique si l'utilisateur revient sur le hub (/shop ou /)
  useEffect(() => {
    const isHub =
      location.pathname === '/shop' || location.pathname === '/' || location.pathname === '/shop/';
    if (isHub && state.page !== null) {
      setState({ page: null, basePath: '' });
    }
  }, [location.pathname, state.page]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const next = searchInput.trim();
      const currentQ = searchParams.get('q') ?? '';
      if (next === currentQ) return;

      if (next) {
        if (location.pathname !== state.basePath && state.basePath) {
          navigate(`${state.basePath}?q=${encodeURIComponent(next)}`);
        } else {
          setSearchParams(
            (prev) => {
              const p = new URLSearchParams(prev);
              p.set('q', next);
              p.delete('page');
              return p;
            },
            { replace: true },
          );
        }
      } else if (currentQ) {
        if (location.pathname === state.basePath) {
          setSearchParams(
            (prev) => {
              const p = new URLSearchParams(prev);
              p.delete('q');
              p.delete('page');
              return p;
            },
            { replace: true },
          );
        }
      }
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchInput, location.pathname, state.basePath, navigate, setSearchParams, searchParams]);

  const restaurantMode = isRestaurantStorefront(state.page);

  const restaurantPaletteVars = useMemo(() => {
    if (!restaurantMode) return {};
    return resolvePageRestaurantPalette(state.page, 'light');
  }, [restaurantMode, state.page]);

  const theme = storefrontTheme(state.page?.accent_color);

  const accentColor = restaurantMode
    ? (restaurantPaletteVars['--store-accent'] ?? theme.accent)
    : theme.accent;
  const accentForeground = restaurantMode
    ? (restaurantPaletteVars['--store-accent-foreground'] ?? theme.foreground)
    : theme.foreground;
  const surfaceColor = restaurantMode ? restaurantPaletteVars['--store-surface'] : undefined;
  const surfaceLowColor = restaurantMode ? restaurantPaletteVars['--store-surface-low'] : undefined;
  const borderColor = restaurantMode ? restaurantPaletteVars['--store-border'] : undefined;
  const inkColor = restaurantMode ? restaurantPaletteVars['--store-ink'] : undefined;
  const inkMutedColor = restaurantMode ? restaurantPaletteVars['--store-ink-muted'] : undefined;
  const bgColor = restaurantMode ? restaurantPaletteVars['--store-bg'] : undefined;

  useEffect(() => {
    const root = document.documentElement;
    if (accentColor) root.style.setProperty('--store-accent', accentColor);
    if (accentForeground) root.style.setProperty('--store-accent-foreground', accentForeground);
    if (surfaceColor) root.style.setProperty('--store-surface', surfaceColor);
    if (surfaceLowColor) root.style.setProperty('--store-surface-low', surfaceLowColor);
    if (borderColor) root.style.setProperty('--store-border', borderColor);
    if (inkColor) root.style.setProperty('--store-ink', inkColor);
    if (inkMutedColor) root.style.setProperty('--store-ink-muted', inkMutedColor);
    if (bgColor) root.style.setProperty('--store-bg', bgColor);
    for (const [key, value] of Object.entries(restaurantPaletteVars)) {
      if (value) root.style.setProperty(key, value);
    }
  }, [
    accentColor,
    accentForeground,
    surfaceColor,
    surfaceLowColor,
    borderColor,
    inkColor,
    inkMutedColor,
    bgColor,
    restaurantPaletteVars,
  ]);

  const showChrome = Boolean(state.page && !state.page.locked);
  const storefrontPath = state.basePath
    ? location.pathname.slice(state.basePath.length).replace(/^\/+/, '')
    : '';
  const checkoutStep = storefrontPath === 'panier' ? 0 : storefrontPath === 'commande' ? 1 : 2;
  const isCheckoutRoute =
    storefrontPath === 'panier' ||
    storefrontPath === 'commande' ||
    storefrontPath.startsWith('confirmation') ||
    storefrontPath.startsWith('paiement/retour');
  const isProductRoute = Boolean(
    state.basePath && location.pathname.startsWith(`${state.basePath}/produit/`),
  );
  const showGlobalChrome = showChrome && !isCheckoutRoute;
  const carts = useStorefrontCart((store) => store.carts);
  const count = cartCount(selectCartLines(carts, state.page?.id));
  const reservationMode = state.page?.mode === 'reservation';
  const isContentRoute =
    !state.basePath ||
    location.pathname === state.basePath ||
    location.pathname.startsWith(`${state.basePath}/produit/`);
  const suppressDiscovery = Boolean(state.page && !isContentRoute);
  const footer = state.page?.footer ?? {
    enabled: true,
    tagline: null,
    show_contact: true,
    show_social_links: true,
    show_powered_by: true,
  };

  const hasFooterContact = Boolean(
    (footer.show_contact &&
      (state.page?.contact_phone || state.page?.contact_email || state.page?.whatsapp_number)) ||
    (footer.show_social_links &&
      state.page?.social_links &&
      Object.values(state.page.social_links).some(Boolean)),
  );

  return (
    <StorePageContext.Provider
      value={{
        page: state.page,
        basePath: state.basePath,
        currency: state.page?.shop?.currency ?? 'XOF',
        restaurantMode,
        restaurantThemeMode: 'light',
        toggleRestaurantThemeMode: () => {},
        setContext: setState,
      }}
    >
      {suppressDiscovery && (
        <Helmet>
          <title>Espace privé · Velko Business</title>
          <meta name="robots" content="noindex, nofollow, noarchive" />
        </Helmet>
      )}
      <div
        data-storefront-root
        style={{
          ['--store-accent' as string]: accentColor,
          ['--store-accent-foreground' as string]: accentForeground,
          ['--store-surface' as string]: surfaceColor,
          ['--store-surface-low' as string]: surfaceLowColor,
          ['--store-border' as string]: borderColor,
          ['--store-ink' as string]: inkColor,
          ['--store-ink-muted' as string]: inkMutedColor,
          ['--store-bg' as string]: bgColor,
          ...restaurantPaletteVars,
        }}
        className={`flex min-h-screen flex-col ${
          restaurantMode
            ? 'bg-[var(--store-bg)] text-[var(--store-ink)] font-restaurant-body antialiased'
            : 'bg-slate-50 text-charcoal-950 dark:bg-charcoal-950 dark:text-charcoal-50'
        }`}
      >
        {showGlobalChrome && (
          <StorefrontHeader
            page={state.page}
            basePath={state.basePath}
            isSearchOpen={isSearchOpen}
            setIsSearchOpen={setIsSearchOpen}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
          />
        )}

        {showChrome && isCheckoutRoute && state.page && (
          <CheckoutFocusHeader
            basePath={state.basePath}
            pageName={state.page.name}
            logoUrl={state.page.logo_url}
            step={checkoutStep}
          />
        )}

        <main
          className={
            showChrome
              ? isCheckoutRoute
                ? 'mx-auto w-full min-w-0 max-w-5xl flex-1 px-4 pb-[calc(2rem+env(safe-area-inset-bottom))] pt-5 sm:pt-8 overflow-x-clip'
                : 'mx-auto w-full max-w-7xl flex-1 px-4 pb-24 pt-4 overflow-x-clip'
              : 'w-full flex-1 overflow-x-clip'
          }
        >
          <Outlet />
        </main>

        <StoreStrictTableClaimDialog />

        {showGlobalChrome && state.page && (
          <div className="mx-auto w-full max-w-7xl px-4" data-storefront-footer-media>
            <MediaAtPlacement
              media={state.page.media}
              placement="footer"
              fallbackCover={state.page.cover_image_url}
            />
          </div>
        )}

        {showGlobalChrome && footer.enabled && state.page && (
          <footer
            className={`border-t ${
              restaurantMode
                ? 'border-[var(--store-border)] bg-[var(--store-surface-low)] text-[var(--store-ink)]'
                : 'border-charcoal-800 bg-charcoal-950 text-white'
            }`}
          >
            <div
              className={`mx-auto grid w-full max-w-5xl gap-6 px-4 py-6 sm:grid-cols-2 ${hasFooterContact ? 'lg:grid-cols-3' : ''}`}
            >
              <div>
                <Link
                  to={state.basePath}
                  className="inline-flex min-h-11 items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)]"
                >
                  {state.page.logo_url ? (
                    <img
                      src={state.page.logo_url}
                      alt=""
                      className="size-10 rounded-xl object-cover"
                    />
                  ) : (
                    <span
                      aria-hidden
                      className="grid size-10 place-items-center rounded-xl font-bold text-[var(--store-accent-foreground)]"
                      style={{ backgroundColor: 'var(--store-accent)' }}
                    >
                      {state.page.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span
                    className={`font-semibold ${restaurantMode ? 'font-restaurant-display text-base text-[var(--store-ink)]' : ''}`}
                  >
                    {state.page.name}
                  </span>
                </Link>
                <p
                  className={`mt-3 max-w-md text-sm leading-6 ${restaurantMode ? 'text-[var(--store-ink-muted)]' : 'text-slate-300'}`}
                >
                  {footer.tagline || state.page.description || t('footer_default_tagline')}
                </p>
              </div>

              <nav aria-label={t('footer_navigation')}>
                <p
                  className={`mb-2 text-xs font-semibold uppercase tracking-wider ${restaurantMode ? 'text-[var(--store-accent)]' : 'text-slate-400'}`}
                >
                  {t('footer_navigation')}
                </p>
                <div className="grid">
                  <FooterLink to={state.basePath} restaurantMode={restaurantMode}>
                    {t('shop')}
                  </FooterLink>
                  <FooterLink
                    to={`${state.basePath}/${reservationMode ? 'reservation' : 'panier'}`}
                    restaurantMode={restaurantMode}
                  >
                    {reservationMode ? t('selection_short') : t('cart_short')}
                  </FooterLink>
                  <FooterLink
                    to={`${state.basePath}/${reservationMode ? 'mes-reservations' : 'mes-commandes'}`}
                    restaurantMode={restaurantMode}
                  >
                    {reservationMode ? t('reservations_short') : t('orders_short')}
                  </FooterLink>
                </div>
              </nav>

              {hasFooterContact && (
                <div>
                  <p
                    className={`mb-2 text-xs font-semibold uppercase tracking-wider ${restaurantMode ? 'text-[var(--store-accent)]' : 'text-slate-400'}`}
                  >
                    {t('footer_contact')}
                  </p>
                  <div className="grid">
                    {footer.show_contact && state.page.contact_phone && (
                      <FooterExternal
                        href={`tel:${state.page.contact_phone}`}
                        restaurantMode={restaurantMode}
                      >
                        <Phone className="size-4" aria-hidden /> {state.page.contact_phone}
                      </FooterExternal>
                    )}
                    {footer.show_contact && state.page.contact_email && (
                      <FooterExternal
                        href={`mailto:${state.page.contact_email}`}
                        restaurantMode={restaurantMode}
                      >
                        <Mail className="size-4" aria-hidden /> {state.page.contact_email}
                      </FooterExternal>
                    )}
                    {footer.show_contact && state.page.whatsapp_number && (
                      <FooterExternal
                        href={`https://wa.me/${state.page.whatsapp_number.replace(/\D/g, '')}`}
                        restaurantMode={restaurantMode}
                      >
                        <MessageCircle className="size-4" aria-hidden /> WhatsApp
                      </FooterExternal>
                    )}
                    {footer.show_social_links &&
                      state.page?.social_links_position !== 'header' &&
                      Object.entries(state.page.social_links ?? {}).map(([network, url]) =>
                        url ? (
                          <FooterExternal key={network} href={url} restaurantMode={restaurantMode}>
                            {network.charAt(0).toUpperCase() + network.slice(1)}
                          </FooterExternal>
                        ) : null,
                      )}
                  </div>
                </div>
              )}
            </div>
            {footer.show_powered_by && (
              <div
                className={`border-t px-4 py-3 text-center ${restaurantMode ? 'border-[var(--store-border)]' : 'border-charcoal-800'}`}
              >
                <a
                  href="https://velko-pos.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex min-h-11 items-center text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)] ${
                    restaurantMode
                      ? 'text-[var(--store-ink-muted)] hover:text-[var(--store-accent)]'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {t('powered_by')}
                </a>
              </div>
            )}
          </footer>
        )}

        {showGlobalChrome && state.basePath && !isProductRoute && (
          <nav
            aria-label={t('mobile_navigation')}
            className={`fixed inset-x-0 bottom-0 z-40 grid h-[calc(4rem+env(safe-area-inset-bottom))] grid-cols-4 border-t pb-[env(safe-area-inset-bottom)] backdrop-blur-2xl md:hidden ${
              restaurantMode
                ? 'border-[var(--store-border)] bg-[var(--store-surface)]/95 text-[var(--store-ink)]'
                : 'border-slate-200 bg-white/95 text-slate-900 dark:border-charcoal-800 dark:bg-charcoal-900/95 dark:text-white'
            }`}
          >
            <MobileNavItem
              to={state.basePath}
              label={t('shop')}
              active={location.pathname === state.basePath}
              restaurantMode={restaurantMode}
              icon={<Home className="size-5" />}
            />
            <button
              type="button"
              onClick={() => {
                if (location.pathname !== state.basePath) navigate(state.basePath);
                setIsSearchOpen(true);
              }}
              className={`flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--store-accent)] ${
                restaurantMode
                  ? 'text-[#d0c5af] hover:text-[#f2ca50]'
                  : 'text-slate-600 dark:text-slate-300'
              }`}
            >
              <Search className="size-5" aria-hidden /> {t('search')}
            </button>
            <MobileNavItem
              to={`${state.basePath}/${reservationMode ? 'mes-reservations' : 'mes-commandes'}`}
              label={reservationMode ? t('reservations_short') : t('orders_short')}
              active={location.pathname.includes(
                reservationMode ? 'mes-reservations' : 'mes-commandes',
              )}
              restaurantMode={restaurantMode}
              icon={<History className="size-5" />}
            />
            <MobileNavItem
              to={`${state.basePath}/${reservationMode ? 'reservation' : 'panier'}`}
              label={reservationMode ? t('selection_short') : t('cart_short')}
              active={location.pathname.endsWith(reservationMode ? '/reservation' : '/panier')}
              restaurantMode={restaurantMode}
              count={count}
              icon={<ShoppingBag className="size-5" />}
            />
          </nav>
        )}
      </div>
    </StorePageContext.Provider>
  );
}

function CheckoutFocusHeader({
  basePath,
  pageName,
  logoUrl,
  step,
}: {
  basePath: string;
  pageName: string;
  logoUrl: string | null;
  step: number;
}) {
  const { t } = useTranslation('storefront');
  const backTo = step === 0 ? basePath : step === 1 ? `${basePath}/panier` : basePath;
  const labels = [
    t('checkout_step_cart'),
    t('checkout_step_details'),
    t('checkout_step_confirmation'),
  ];

  return (
    <header className="relative overflow-hidden border-b border-[var(--store-border)] bg-[var(--store-surface)]/95 backdrop-blur-xl">
      <div className="mx-auto w-full max-w-5xl px-4 py-3">
        <div className="flex min-h-11 items-center gap-3">
          <Link
            to={backTo}
            aria-label={t('checkout_back')}
            className="store-flow-press grid size-11 shrink-0 place-items-center rounded-[var(--store-radius-control)] border border-[var(--store-border)] bg-[var(--store-surface)] text-[var(--store-ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)]"
          >
            <ArrowLeft className="size-5" aria-hidden />
          </Link>
          <Link
            to={basePath}
            aria-label={t('checkout_home', { name: pageName })}
            className="store-flow-press grid size-11 shrink-0 place-items-center rounded-[var(--store-radius-control)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)]"
          >
            {logoUrl ? (
              <img src={logoUrl} alt="" className="size-9 rounded-xl object-cover" />
            ) : (
              <span
                aria-hidden
                className="grid size-9 place-items-center rounded-xl text-sm font-black text-[var(--store-accent-foreground)]"
                style={{ backgroundColor: 'var(--store-accent)' }}
              >
                {pageName.charAt(0).toUpperCase()}
              </span>
            )}
          </Link>
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-[var(--store-ink)]">{pageName}</p>
            <p className="text-xs text-[var(--store-ink-muted)]">{t('checkout_secure')}</p>
          </div>
        </div>

        <ol aria-label={t('checkout_progress')} className="mt-3 grid grid-cols-3 gap-2">
          {labels.map((label, index) => (
            <li
              key={label}
              aria-current={index === step ? 'step' : undefined}
              className="min-w-0 text-center"
            >
              <div className="mb-1.5 h-1 overflow-hidden rounded-full bg-[var(--store-border,theme(colors.slate.200))]">
                <div
                  className="h-full rounded-full transition-[width] duration-[var(--store-motion-dock)] motion-reduce:transition-none"
                  style={{
                    backgroundColor: 'var(--store-accent)',
                    width: index <= step ? '100%' : '0%',
                  }}
                />
              </div>
              <span
                aria-label={label}
                className={`flex min-w-0 items-center justify-center gap-1 text-center text-[10px] font-semibold sm:text-xs ${
                  index <= step
                    ? 'text-[var(--store-ink)] font-bold'
                    : 'text-[var(--store-ink-muted)] opacity-70'
                }`}
              >
                {index < step && <Check className="size-3 shrink-0" aria-hidden />}
                <span className="truncate">{label}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>
      <StorefrontFlowline className="absolute inset-x-0 bottom-0 h-3 opacity-35" />
    </header>
  );
}

function FooterLink({
  to,
  children,
  restaurantMode,
}: {
  to: string;
  children: ReactNode;
  restaurantMode?: boolean;
  isLightRestaurant?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`inline-flex min-h-11 items-center text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)] ${
        restaurantMode
          ? 'text-[var(--store-ink-muted)] hover:text-[var(--store-accent)]'
          : 'text-slate-300 hover:text-white'
      }`}
    >
      {children}
    </Link>
  );
}

function FooterExternal({
  href,
  children,
  restaurantMode,
}: {
  href: string;
  children: ReactNode;
  restaurantMode?: boolean;
  isLightRestaurant?: boolean;
}) {
  return (
    <a
      href={href}
      target={href.startsWith('http') ? '_blank' : undefined}
      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      className={`inline-flex min-h-11 items-center gap-2 break-all text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent)] ${
        restaurantMode
          ? 'text-[var(--store-ink-muted)] hover:text-[var(--store-accent)]'
          : 'text-slate-300 hover:text-white'
      }`}
    >
      {children}
    </a>
  );
}

function MobileNavItem({
  to,
  label,
  icon,
  active,
  count,
  restaurantMode,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  active: boolean;
  count?: number;
  restaurantMode?: boolean;
  isLightRestaurant?: boolean;
}) {
  return (
    <Link
      to={to}
      className={`relative flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--store-accent)] ${
        restaurantMode
          ? active
            ? 'text-[var(--store-accent)] font-bold'
            : 'text-[var(--store-ink-muted)] hover:text-[var(--store-ink)]'
          : active
            ? 'text-charcoal-950 dark:text-white'
            : 'text-slate-600 dark:text-slate-300'
      }`}
    >
      <span
        className={`relative grid size-7 place-items-center rounded-lg ${
          active && restaurantMode ? 'bg-[var(--store-accent)]/15 text-[var(--store-accent)]' : ''
        }`}
        style={
          active && !restaurantMode
            ? {
                backgroundColor: 'var(--store-accent)',
                color: 'var(--store-accent-foreground)',
              }
            : undefined
        }
      >
        {icon}
        {count ? (
          <span
            className={`absolute -right-3 -top-2 min-w-5 rounded-full px-1 text-center font-mono text-[9px] leading-5 ${
              restaurantMode
                ? 'bg-[var(--store-accent)] text-[var(--store-accent-foreground)] font-bold'
                : 'bg-charcoal-950 text-white dark:bg-white dark:text-charcoal-950'
            }`}
          >
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </span>
      {label}
    </Link>
  );
}
